import React from "react"
import { useTranslations } from "@/hooks/useTranslations"
import { useAuth } from "@/hooks/useAuth"
import LoginForm from "@/components/LoginForm"
import { ModuleNotActiveScreen } from "./ModuleNotActiveScreen"
import { UserRole } from "@/types/reports"
import { OptimGovPageLoader } from "@/components/ui/optimgov-loader"
import { Button } from "@/components/ui/button"
import { resetAuthSession } from "@optimgov/module-auth"

interface ReportsProtectedRouteProps {
	children: React.ReactNode
	requiredRole?: UserRole
	requiredPermission?: string
}

export const ReportsProtectedRoute: React.FC<ReportsProtectedRouteProps> = ({ children, requiredRole, requiredPermission }) => {
	const t = useTranslations()
	const tLogin = useTranslations("login")
	const { user, reportsUser, hasRole, checkPermission, loading, error, refreshUser } = useAuth()

	if (loading) {
		return <OptimGovPageLoader />
	}

	// /api/users/me failed three times (typically the token could not be
	// acquired). Rendering the children here would remount them, retrigger the
	// load and flash the splash forever; stop and let the user decide.
	if (error?.code === "auth/refresh-error") {
		return (
			<div className='min-h-screen flex flex-col items-center justify-center bg-slate-100 px-4'>
				<img src='/OptimGovLogo.png' alt='OptimGov' width={220} height={50} className='mb-8 h-16 w-auto' />
				<div className='w-full max-w-md rounded-xl bg-white p-6 text-center shadow-lg sm:p-8'>
					<h1 className='mb-2 text-xl font-bold text-gray-700'>{tLogin("sessionRecovery.title")}</h1>
					<p className='mb-6 text-sm text-gray-500'>{tLogin("sessionRecovery.body")}</p>
					<div className='flex flex-col gap-2 sm:flex-row sm:justify-center'>
						<Button variant='outline' onClick={() => void refreshUser()}>{tLogin("sessionRecovery.retry")}</Button>
						<Button onClick={resetAuthSession}>{tLogin("sessionRecovery.signInAgain")}</Button>
					</div>
				</div>
			</div>
		)
	}

	if (error?.code === "auth/module-not-licensed") {
		return <ModuleNotActiveScreen />
	}

	if (!user) {
		return (
			<div className='min-h-screen flex flex-col items-center justify-center bg-slate-100 px-4'>
				<div className='mb-8 flex items-center gap-3'>
					<img src='/OptimGovLogo.png' alt='OptimGov' width={220} height={50} className='h-16 w-auto' />
				</div>
				<LoginForm />
				<div className='mt-8 text-center'>
					<p className='text-sm text-gray-400'>
						&copy; {new Date().getFullYear()} OptimGov. {tLogin("rights")}
					</p>
				</div>
			</div>
		)
	}

	// Check role requirement
	if (requiredRole && !hasRole(requiredRole)) {
		return (
			<div className='min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8'>
				<div className='text-center'>
					<h2 className='text-2xl font-bold text-gray-900'>{t("auth.accessDenied")}</h2>
					<p className='mt-2 text-gray-600'>{t("auth.insufficientRole", { role: requiredRole })}</p>
					<p className='mt-1 text-sm text-gray-500'>{t("auth.currentRole", { role: reportsUser?.role || "Unknown" })}</p>
				</div>
			</div>
		)
	}

	// Check permission requirement
	if (requiredPermission && !checkPermission(requiredPermission)) {
		return (
			<div className='min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8'>
				<div className='text-center'>
					<h2 className='text-2xl font-bold text-gray-900'>{t("auth.accessDenied")}</h2>
					<p className='mt-2 text-gray-600'>{t("auth.insufficientPermission", { permission: requiredPermission })}</p>
				</div>
			</div>
		)
	}

	return <>{children}</>
}
